<template>
  <div>
    <DynamicModal
      :showModal="showModalProp"
      :persistent="false"
      @clickOutside="closeModal"
      title="Periods Import"
      max-width="800px"
    >
      <v-container fluid slot="title-buttons" class="py-1">
        <v-row justify="end">
          <v-btn
            icon
            depressed
            color="primary"
            @click="closeModal"
          >
            <v-icon color="white">close</v-icon>
          </v-btn>
        </v-row>
      </v-container>
      <v-container fluid slot="content">
        <v-form ref="form">
          <v-row class="mt-3">
            <v-col cols="12" sm="5">
              The file must have the extensions .xlsx or .xls and contain the following three columns with their respective headers:
              <ul>
                <li style="list-style: none">
                  Period
                  <ul>
                    <li>Header: "period"</li>
                    <li>Content: Number of the period, it's a number between 1 and 26.</li>
                  </ul>
                </li>
                <li style="list-style: none">
                  Date Begin
                  <ul>
                    <li>Header: "dateBegin"</li>
                    <li>Content: The period's begin date in format 'mm/dd/yyyy'</li>
                  </ul>
                </li>
                <li style="list-style: none">
                  Date End
                  <ul>
                    <li>Header: "dateEnd"</li>
                    <li>Content: The period's end date in format 'mm/dd/yyyy'</li>
                  </ul>
                </li>
              </ul>
            </v-col>
            <v-col cols="12" sm="7">
              <v-row>
                <v-col cols="12" sm="4" md="4" class="pb-0">
                  <v-text-field
                    label="Year"
                    v-model="year"
                    validate-on-blur
                    outlined
                    :maxlength="4"
                    :rules="[required('Year')]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="8" md="8" class="pb-0">
                  <v-file-input
                    v-model="fileInput"
                    accept=".xlsx,.xls"
                    @change="biweeklyPeriodsFileUploaded"
                    outlined
                    hide-details
                  ></v-file-input>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-data-table
                    dense
                    :headers="biweeklyPeriodsHeaders"
                    :items="biweeklyPeriods"
                    :items-per-page="10"
                  >
                    <template v-slot:item.dateBegin="{ item }">
                      {{ formatFileDate(item.dateBegin) }}
                    </template>
                    <template v-slot:item.dateEnd="{ item }">
                      {{ formatFileDate(item.dateEnd) }}
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
      <v-container fluid slot="buttons" class="pb-0 mb-0">
        <div class="row d-flex justify-end">
          <div class="col-6">
            <v-btn
              block
              depressed
              color="primary"
              @click="savePeriodsHandler"
            >
              Save
            </v-btn>
          </div>
          <div class="col-6">
            <v-btn
              block
              depressed
              outlined
              @click="closeModal"
            >
              Cancel
            </v-btn>
          </div>
        </div>
      </v-container>
    </DynamicModal>
    <NotificationsSnackBar
    ></NotificationsSnackBar>
  </div>
</template>

<script>
import { DynamicModal } from '@/components/dialogs'
import SimplePageHeader from '@/components/pageComponents/SimplePageHeader'
import { FormattedNumberField } from '@/components/shared/textFields'
import { read } from '@/lib/export/excel'
import { mapActions } from 'vuex'
import NotificationsSnackBar from '@/components/notifications/NotificationsSnackBar'
import { mapFields } from '@/vueHelpers/vuex'
import { required } from '@/lib/formValidations'

export default {
  components: {
    NotificationsSnackBar,
    DynamicModal,
    SimplePageHeader,
    FormattedNumberField
  },
  data () {
    return {
      showModalProp: false,
      fileInput: null,
      year: null,
      biweeklyPeriodsHeaders: [
        { text: 'Period', value: 'period', align: 'center', width: '20%', sortable: false },
        { text: 'Date Begin', value: 'dateBegin', align: 'center', width: '40%', sortable: false },
        { text: 'Date End', value: 'dateEnd', align: 'center', width: '40%', sortable: false }
      ],
      biweeklyPeriods: []
    }
  },
  computed: {
    ...mapFields('notifications', [
      'color',
      'showNotification',
      'text',
      'timeout'
    ])
  },
  methods: {
    required,
    ...mapActions('biweeklyPeriods', ['savePeriods']),
    showModal (year) {
      this.showModalProp = true
      this.year = year
    },
    closeModal () {
      this.showModalProp = false
      this.fileInput = null
      this.biweeklyPeriods = []
      this.$emit('closeModal')
    },
    async biweeklyPeriodsFileUploaded () {
      if (this.fileInput) {
        let result = read(await this.fileInput.arrayBuffer(), 'buffer')
        if (!result.header.includes('period') ||
          !result.header.includes('dateBegin') ||
          !result.header.includes('dateEnd')) {
          this.showNotification = true
          this.text = 'The uploaded file doesn\'t contain the required columns names'
          this.color = 'error'
          this.timeout = '3000'
          this.fileInput = null
          return
        }
        if (result.results.length !== 26) {
          this.showNotification = true
          this.text = 'The uploaded file doesn\'t contain the 26 periods'
          this.color = 'error'
          this.timeout = '3000'
          this.fileInput = null
          return
        }
        try {
          for (const row of result.results) {
            // Check if the Period is ok
            if (isNaN(parseInt(row.period)) ||
              !(parseInt(row.period) >= 1 && parseInt(row.period) <= 26)) {
              throw new TypeError()
            }

            // Check if the Date Begin is ok
            let dateBegin = new Date(row.dateBegin)
            dateBegin.toISOString()

            // Check if the Date End is ok
            let dateEnd = new Date(row.dateEnd)
            dateEnd.toISOString()
          }
        } catch (exception) {
          if (exception instanceof TypeError) {
            this.showNotification = true
            this.text = 'The format of the Period is not correct it must be an integer between 1 and 26.'
            this.color = 'error'
            this.timeout = '6000'
            this.fileInput = null
            return
          }
          if (exception instanceof RangeError) {
            this.showNotification = true
            this.text = 'The format of the Date Begin or Date End is not correct. It must be mm/dd/yyyy'
            this.color = 'error'
            this.timeout = '6000'
            this.fileInput = null
            return
          }
        }
        this.biweeklyPeriods = result.results
      } else {
        this.biweeklyPeriods = []
      }
    },
    savePeriodsHandler () {
      if (this.$refs.form.validate()) {
        if (this.fileInput) {
          this.biweeklyPeriods = this.biweeklyPeriods.map((period) => ({
            ...period, year: this.year
          }))
          this.savePeriods(this.biweeklyPeriods)
          this.$emit('periodsImported', this.biweeklyPeriods, this.year)
          this.closeModal()
        } else {
          this.showNotification = true
          this.text = 'There is no file to process'
          this.color = 'error'
        }
      }
    },
    formatFileDate (date) {
      return new Date(date).toLocaleDateString('en-US')
    }
  }
}
</script>
